import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { getDatabase, ref, get, update } from 'firebase/database';
import './Settings.css';
import { fetchContact, updateOwnContact } from '../contacts';
import { BlockedUsersList } from '../Safety/Safety';
import { TERMS_URL, PRIVACY_URL } from '../safety';
import { useDeleteAccount } from './DeleteAccount';
import { isAdmin } from '../admin';

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const ChevronUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);

function SettingsItem({ icon, label, sub, onClick, danger, expanded, children }) {
  return (
    <>
      <div className={`settings-item${danger ? ' danger' : ''}`} onClick={onClick}>
        <div className="settings-item-left">
          <div className="settings-item-icon">{icon}</div>
          <div>
            <div className="settings-item-label">{label}</div>
            {sub && <div className="settings-item-sub">{sub}</div>}
          </div>
        </div>
        <div className="settings-item-chevron">
          {children ? (expanded ? <ChevronUpIcon /> : <ChevronIcon />) : <ChevronIcon />}
        </div>
      </div>
      {expanded && children && (
        <div className="settings-item-expand">{children}</div>
      )}
    </>
  );
}

function Settings() {
  const navigate  = useNavigate();
  const auth      = getAuth();
  const db        = getDatabase();
  const uid       = auth.currentUser?.uid;

  /* which panel is open */
  const [openPanel, setOpenPanel] = useState(null);

  /* edit profile state */
  const [profile,  setProfile]  = useState({ name: '', username: '', number: '', color: '', make: '', model: '' });
  const deleteAccount = useDeleteAccount();
  const [saving,   setSaving]   = useState(false);
  const [saveMsg,  setSaveMsg]  = useState('');

  /* load current user data */
  useEffect(() => {
    if (!uid) return;
    get(ref(db, `users/${uid}`)).then(async snap => {
      if (snap.exists()) {
        const d = snap.val();
        // Phone lives in the private contact entry (falls back to the old copy).
        const { number } = await fetchContact(uid);
        const car = d.car || {};
        setProfile({ name: d.name || '', username: d.username || '', number: number || '',
          color: car.color || '', make: car.make || '', model: car.model || '' });
      }
    }).catch(() => {});
  }, [uid, db]);

  const toggle = (panel) => setOpenPanel(p => p === panel ? null : panel);

  const handleSaveProfile = async () => {
    if (!uid) return;
    setSaving(true); setSaveMsg('');
    try {
      // Number goes to the private contact entry first; only then is the old
      // public copy removed, so a failure never loses it.
      await updateOwnContact({ number: profile.number.trim() });
      // Car shows on your rides' booking screen; leaving all three empty removes it.
      const car = Object.fromEntries(['make', 'model', 'color']
        .map(k => [k, profile[k].trim().slice(0, 40)]).filter(([, v]) => v));
      await update(ref(db, `users/${uid}`), {
        name:     profile.name.trim(),
        username: profile.username.trim(),
        number:   null,
        car:      Object.keys(car).length ? car : null,
      });
      setSaveMsg('Saved!');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch {
      setSaveMsg('Failed to save. Try again.');
    }
    setSaving(false);
  };

  const handlePasswordReset = async () => {
    const email = auth.currentUser?.email;
    if (!email) { alert('No email on file.'); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`Password reset email sent to ${email}`);
    } catch (e) {
      alert('Failed: ' + e.message);
    }
  };

  const handleLogout = () => {
    signOut(auth).then(() => navigate('/')).catch(err => console.error(err));
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <button className="settings-back-btn" onClick={() => navigate('/profile')}>← Back</button>
        <h1>Settings</h1>
      </div>

      {/* ── Account ── */}
      <div className="settings-group">
        <div className="settings-group-label">Account</div>
        <div className="settings-list">

          {/* Edit Profile — expandable */}
          <SettingsItem
            expanded={openPanel === 'profile'}
            onClick={() => toggle('profile')}
            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
            label="Edit Profile"
            sub="Name, username, phone and car"
          >
            <div className="settings-form">
              <label className="settings-form-label">Name</label>
              <input className="settings-form-input" value={profile.name}
                onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                placeholder="Your full name" />

              <label className="settings-form-label">Username</label>
              <input className="settings-form-input" value={profile.username}
                onChange={e => setProfile(p => ({ ...p, username: e.target.value }))}
                placeholder="@username" />

              <label className="settings-form-label">Phone number</label>
              <input className="settings-form-input" value={profile.number}
                onChange={e => setProfile(p => ({ ...p, number: e.target.value }))}
                placeholder="Your phone number" type="tel" />

              <label className="settings-form-label">Your car (shown to riders who book with you)</label>
              <div className="settings-form-car">
                <input className="settings-form-input" value={profile.color} maxLength={40}
                  onChange={e => setProfile(p => ({ ...p, color: e.target.value }))} placeholder="Color" />
                <input className="settings-form-input" value={profile.make} maxLength={40}
                  onChange={e => setProfile(p => ({ ...p, make: e.target.value }))} placeholder="Make" />
                <input className="settings-form-input" value={profile.model} maxLength={40}
                  onChange={e => setProfile(p => ({ ...p, model: e.target.value }))} placeholder="Model" />
              </div>

              <div className="settings-form-row">
                <button className="settings-save-btn" onClick={handleSaveProfile} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                {saveMsg && <span className={`settings-save-msg${saveMsg === 'Saved!' ? ' ok' : ' err'}`}>{saveMsg}</span>}
              </div>
            </div>
          </SettingsItem>

          {/* Change Password */}
          <SettingsItem
            onClick={handlePasswordReset}
            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
            label="Change Password"
            sub="Send a reset link to your email"
          />

          {/* Blocked users */}
          <SettingsItem
            expanded={openPanel === 'blocked'}
            onClick={() => toggle('blocked')}
            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>}
            label="Blocked users"
            sub="People whose rides and requests you've hidden"
          >
            <BlockedUsersList />
          </SettingsItem>

        </div>
      </div>

      {isAdmin(uid) && (
        <div className="settings-group">
          <div className="settings-group-label">Owner</div>
          <div className="settings-list">
            <button className="settings-admin-link" onClick={() => navigate('/admin')}>Open admin page</button>
          </div>
        </div>
      )}

      {/* ── App ── */}
      <div className="settings-group">
        <div className="settings-group-label">App</div>
        <div className="settings-list">
          <SettingsItem
            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
            label="About FalconRide"
            sub="Terms of Use and Privacy Policy"
            expanded={openPanel === 'about'}
            onClick={() => toggle('about')}
          >
            <div className="settings-links">
              <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Use</a>
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </div>
          </SettingsItem>
        </div>
      </div>

      {/* ── Danger zone ── */}
      <div className="settings-group">
        <div className="settings-list">
          <SettingsItem
            danger
            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>}
            label="Log Out"
            onClick={handleLogout}
          />
          <SettingsItem
            danger
            icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>}
            label="Delete account"
            sub="Permanently remove your profile, posts and bookings"
            onClick={deleteAccount.start}
          />
        </div>
      </div>
      {deleteAccount.modal}
    </div>
  );
}

export default Settings;
